import { ReactElement, useMemo } from 'react';
import {
    UIPaginatedFilterItem,
    UIPaginatedFilterOption,
    UIPopoverPlacement
} from '@/ui';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '../providers';
import { useFetchDeviceFilterOptions } from '@/common/queries';
import { useFilterPopupState } from '@/common/hooks';
import { useTasksTranslation } from '../hooks';

type DeviceFilterItemProps = {
    popoverPlacement?: UIPopoverPlacement;
};

export function DeviceFilterItem(props: DeviceFilterItemProps): ReactElement {
    const { popoverPlacement } = props;

    const { t } = useTasksTranslation();
    const { company } = useCompanyContext();
    const { teamIds, deviceIds, onDeviceFilterApplied } = useTaskFilter();
    const { search, setSearch, debouncedSearch, hasOpened, onOpenChanged } =
        useFilterPopupState();

    const scopedTeamIds = useMemo(() => Array.from(teamIds), [teamIds]);

    const {
        options,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchDeviceFilterOptions({
        companyId: company.id,
        search: debouncedSearch,
        teamIds: scopedTeamIds,
        enabled: hasOpened
    });

    const items = useMemo<UIPaginatedFilterOption[]>(() => {
        return options.map((option) => ({
            id: option.id,
            text: option.name,
            image: option.image ?? undefined,
            subAvatar: option.user
                ? {
                      img: option.user.image || '',
                      alt: option.user.name
                  }
                : undefined
        }));
    }, [options]);

    const selectedIds = useMemo(() => Array.from(deviceIds), [deviceIds]);

    return (
        <UIPaginatedFilterItem
            noneSelectedText={t('components.device_filter_item.none_selected')}
            emptyItemsText={t('components.device_filter_item.empty')}
            icon="device"
            popoverPlacement={popoverPlacement}
            items={items}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            isLoading={hasOpened && status === 'pending'}
            isError={status === 'error'}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onLoadMore={fetchNextPage}
            onOpenChanged={onOpenChanged}
            onApply={onDeviceFilterApplied}
            onRetry={refetch}
        />
    );
}
